import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppAuth, useAppAuthActions, useAppTenantsState } from '@/hooks/useAppAuth';
import AuthService from '@/services/auth.service';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight } from 'lucide-react';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

export default function TenantSwitcher() {
  const { user } = useAppAuth();
  const { switchTenant } = useAppAuthActions();
  const { tenants, loading } = useAppTenantsState();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(undefined);
  const [switching, setSwitching] = useState(false);

  const options: ComboboxOption[] = useMemo(
    () => (tenants || []).map((t) => ({ value: t.tenantId, label: t.name })),
    [tenants]
  );

  if (loading || !tenants || tenants.length < 2) {
    return null;
  }

  const currentTenantId = user?.tenantId;
  const currentTenantName = options.find((o) => o.value === currentTenantId)?.label || 'Unknown';

  const handleOpenDialog = () => {
    setSelectedTenantId(currentTenantId);
    setDialogOpen(true);
  };

  const handleSwitch = () => {
    if (!selectedTenantId || selectedTenantId === currentTenantId) return;
    const authService = AuthService.getInstance();

    if (authService.isCustomDomain) {
      // Switching tenants on a custom domain — redirect to the main domain
      // with switchTo param. The main domain handles the Frontegg tenant
      // switch before loading config (which may redirect to a different
      // custom domain). Don't call switchTenant() here — it's async and
      // races with the redirect.
      const switchTo = encodeURIComponent(selectedTenantId);
      window.location.href = `${authService.authAppUrl}/?switchTo=${switchTo}`;
      return;
    }

    setSwitching(true);
    switchTenant({
      tenantId: selectedTenantId,
      callback: (success: boolean) => {
        if (success) {
          queryClient.removeQueries();
          navigate('/');
        }
        setSwitching(false);
        setDialogOpen(false);
      },
    } as any);
  };

  return (
    <>
      <div className="flex items-center justify-between rounded-md border border-sidebar-border px-3 py-2 text-xs">
        <span className="truncate font-medium">{currentTenantName}</span>
        <button
          onClick={handleOpenDialog}
          className="ml-2 shrink-0 rounded-sm p-1 opacity-70 hover:opacity-100 hover:bg-sidebar-accent/5 transition-colors"
          aria-label="Switch tenant"
        >
          <ArrowLeftRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Switch Account</DialogTitle>
            <DialogDescription>Select an account to switch to.</DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Combobox
              options={options}
              value={selectedTenantId}
              onValueChange={setSelectedTenantId}
              placeholder="Select account..."
              emptyText="No accounts found."
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSwitch}
              disabled={switching || !selectedTenantId || selectedTenantId === currentTenantId}
            >
              {switching ? 'Switching...' : 'Change Tenant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
